/**
 * Reading the visitor's acquisition context off the public form's own URL.
 *
 * A merchant shares `/f/:slug` in an ad, so the link that reaches a stranger is
 * `/f/:slug?utm_source=google&utm_medium=cpc&…&gclid=…`. Until this existed the
 * page never looked at the query string at all, so those parameters reached the
 * browser and died there: the server stamped the form slug as the source and
 * the merchant's Ads tab could only ever say "this form", never "this ad".
 *
 * Captured ONCE, at mount. `location.search` and `document.referrer` describe
 * the arrival, and re-reading them at submit time would report whatever the URL
 * had become by then — which for a router-driven page is not the same thing.
 *
 * Kept out of the .tsx on purpose: it is pure, so it can be tested against the
 * server's schema without rendering anything.
 */
import type { PublicFormAttribution } from '../../services/api/formsApi';

/**
 * Every key the server's `attributionSchema` accepts, in its wire spelling.
 * The schema is `.strict()`, so an extra key here is a 400 on a real visitor's
 * submission — this list is the contract, not a convenience.
 */
const URL_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
] as const;

/**
 * Mirrors the server's own ceilings (ATTRIBUTION_TEXT_MAX / _URL_MAX). Sending
 * something the server will reject fails the whole submission and the visitor
 * gets a 400 they cannot act on, so the page truncates rather than gambles.
 * The server still enforces its own caps — this is politeness, not the fence.
 */
const TEXT_MAX = 120;
const URL_MAX = 500;

function clip(raw: string | null, max: number): string | undefined {
  if (raw === null) return undefined;
  const trimmed = raw.trim().slice(0, max);
  return trimmed.length === 0 ? undefined : trimmed;
}

/**
 * Build the attribution payload for this page load, or `undefined` when the
 * visitor arrived with no context at all — in which case the submission body is
 * byte-for-byte what it was before this feature, which is the point.
 */
export function readAttributionFromUrl(
  search: string,
  href: string,
  referrer: string,
): PublicFormAttribution | undefined {
  const params = new URLSearchParams(search);
  const out: PublicFormAttribution = {};
  for (const name of URL_PARAMS) {
    const value = clip(params.get(name), TEXT_MAX);
    if (value !== undefined) out[name] = value;
  }
  // Only worth sending when something identifies the visit. The landing URL of
  // a bare, parameter-free share link says nothing the slug does not already
  // say, and would put a row of noise in the merchant's report.
  if (Object.keys(out).length === 0) return undefined;

  const landingUrl = clip(href, URL_MAX);
  if (landingUrl !== undefined) out.landing_url = landingUrl;
  const ref = clip(referrer, URL_MAX);
  if (ref !== undefined) out.referrer = ref;
  return out;
}
